import {
  Button,
  Card,
  CardBody,
  MenuItem,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { useInvestmentConfirmDelete } from "features/investments/InvestmentDelete";
import {
  useInvestmentEntityStatsRetrieveSuspense,
  useInvestmentTableDataRetrieveSuspense,
} from "gen/endpoints/investment/investment";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import {
  CardSection,
  CardSectionGroup,
} from "shared/components/card/CardSection";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import {
  GetLinkInfoForRowFn,
  RenderRowActionsFn,
} from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { formatMoney } from "shared/utils/MoneyUtils";
import { useHasPermission } from "shared/utils/PermissionUtils";

export const InvestmentsPage: React.FC = () => {
  const { t } = useTranslation(["common", "investment"]);
  const navigate = useNavigate();
  const hasConfigPermission = useHasPermission(PermissionTypeEnum.config);

  const { fundFamilyId } = useRequiredStringParams();

  const { data: tableData } =
    useInvestmentTableDataRetrieveSuspense(fundFamilyId);
  const { data: stats } =
    useInvestmentEntityStatsRetrieveSuspense(fundFamilyId);

  const confirmDeleteInvestment = useInvestmentConfirmDelete({
    fundFamilyId,
    isPortfolio: false,
  });

  const confirmDeletePortfolio = useInvestmentConfirmDelete({
    fundFamilyId,
    isPortfolio: true,
  });

  const getLinkInfoForRow = useCallback<GetLinkInfoForRowFn>(
    ({ row }) => {
      const metadata = row.data?.metadata;
      if (metadata?.type !== "id_row") {
        return undefined;
      }

      return row.data?.granularity_level === "investment"
        ? {
            type: "generic",
            to: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .investments({})
              .investment({ investmentId: metadata.id }).$,
          }
        : {
            type: "generic",
            to: rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .portfolios({})
              .portfolio({ portfolioId: metadata.id }).$,
          };
    },
    [fundFamilyId],
  );

  const renderRowActions = useCallback<RenderRowActionsFn>(
    ({ row }) => ({
      type: "menu",
      actions: (
        <MenuItem
          className="destructive"
          onClick={async () => {
            if (row.data?.granularity_level === "investment") {
              await confirmDeleteInvestment({ investmentId: row.id! });
            } else {
              await confirmDeletePortfolio({ investmentId: row.id! });
            }
          }}
        >
          {t("common:delete")}
        </MenuItem>
      ),
    }),
    [t, confirmDeleteInvestment, confirmDeletePortfolio],
  );

  const addButton = (
    <Restricted permission={PermissionTypeEnum.config}>
      <Button
        size="xs"
        onClick={() =>
          navigate(
            rootRoute({})
              .fundFamilies({})
              .fundFamily({ fundFamilyId })
              .investments({})
              .create({}).$,
          )
        }
      >
        {t("investment:add.button")}
      </Button>
    </Restricted>
  );

  return (
    <PageContent isFullHeight={true}>
      <PageHeader title={t("common:investments")} />
      <PageBody>
        <Card variant="stat" mb="16px">
          <CardBody>
            <CardSectionGroup>
              <CardSection>
                <StatGroup>
                  <Stat size="sm">
                    <StatNumber>{stats.active_investments}</StatNumber>
                    <StatLabel>{t("investment:activeInvestments")}</StatLabel>
                  </Stat>
                  <Stat size="sm">
                    <StatNumber>{formatMoney(stats.called_amount)}</StatNumber>
                    <StatLabel>{t("investment:calledCapital")}</StatLabel>
                  </Stat>
                  <Stat size="sm">
                    <StatNumber>
                      {formatMoney(stats.distributed_amount)}
                    </StatNumber>
                    <StatLabel>{t("investment:distributedCapital")}</StatLabel>
                  </Stat>
                </StatGroup>
              </CardSection>
            </CardSectionGroup>
          </CardBody>
        </Card>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>{t("common:investments")}</TableBoxTitle>
            <DataTableBoxActions createAction={addButton} />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable
              data={tableData}
              getLinkInfoForRow={getLinkInfoForRow}
              renderRowActions={
                hasConfigPermission ? renderRowActions : undefined
              }
              emptyStateProps={{
                title: t("investment:empty.title"),
                description: t("investment:empty.description"),
                children: addButton,
              }}
            />
          </TableBoxBody>
        </TableBox>
      </PageBody>
    </PageContent>
  );
};
